/**
 * Wo man in einem Bereich zuletzt stand.
 *
 * Die Ablage schreibt ihren Ort in die Adresse — Modul, Ordner, offene
 * Datei stehen als `?m=…&f=…&d=…` darin. Das trägt den Zurück-Knopf und
 * ein Lesezeichen, aber nicht den Weg über die Leiste: der Verweis dort
 * zeigt auf das nackte `/storage`, und wer kurz zum Rechner schaut und
 * zurückkommt, steht wieder ganz oben.
 *
 * Hier liegt je Bereich die zuletzt besuchte vollständige Adresse. Die
 * Leiste führt dorthin zurück statt an den Anfang. Weil jede Seite ihren
 * Ort ohnehin in die Adresse schreibt, gilt das für alle zugleich — die
 * offene Datei in der Ablage, das Repository unter Git, die Woche im
 * Kalender.
 *
 * Nur für die Dauer des Reiters (`sessionStorage`): ein neuer Reiter fängt
 * vorn an, und ein Ordner, den es morgen nicht mehr gibt, kommt nicht als
 * Überraschung zurück.
 */

const SCHLUESSEL = "home.letzterOrt";

type Orte = Record<string, string>;

function lesen(): Orte {
  try {
    const roh = sessionStorage.getItem(SCHLUESSEL);
    if (!roh) return {};
    const o: unknown = JSON.parse(roh);
    return o && typeof o === "object" ? (o as Orte) : {};
  } catch {
    // Privater Modus, abgeschalteter Speicher — dann eben ohne Gedächtnis.
    return {};
  }
}

function schreiben(orte: Orte): void {
  try {
    sessionStorage.setItem(SCHLUESSEL, JSON.stringify(orte));
  } catch {
    /* nicht der Rede wert: das Merken ist eine Bequemlichkeit */
  }
}

/** Der Bereich, zu dem eine Adresse gehört — das erste Stück des Pfads.
 *  `/git/sem2?f=x` gehört zu `/git`, `/` zu sich selbst.
 *
 *  Der Frage- und der Rautenteil müssen vorher weg: sonst gehörte
 *  `/storage?m=x` zum Bereich `/storage?m=x` und damit zu keinem. */
export function bereichVon(adresse: string): string {
  const pfad = adresse.split(/[?#]/)[0];
  const erstes = pfad.split("/").filter(Boolean)[0];
  return erstes ? `/${erstes}` : "/";
}

/** Hält fest, wo man gerade steht. Ruft die Leiste beim Wechsel auf. */
export function merkeOrt(pfad: string, suche: string): void {
  const bereich = bereichVon(pfad);
  const orte = lesen();
  orte[bereich] = pfad + (suche || "");
  schreiben(orte);
}

/**
 * Wohin ein Verweis auf `ziel` führen soll.
 *
 * Steht man schon in diesem Bereich, führt er an den Anfang — sonst käme
 * man aus einem tiefen Ordner nie wieder heraus, weil der Verweis auf die
 * Stelle zeigte, auf der man ohnehin steht. Von außerhalb führt er dorthin
 * zurück, wo man zuletzt war.
 */
export function zielFuer(ziel: string, jetzt: string): string {
  const bereich = bereichVon(ziel);
  if (bereichVon(jetzt) === bereich) return ziel;
  const gemerkt = lesen()[bereich];
  return gemerkt && bereichVon(gemerkt) === bereich ? gemerkt : ziel;
}
